import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Eye, Trash2, Filter, ChevronDown, ChevronUp, Search } from 'lucide-react';
import { useTranslation } from '../../context/LanguageContext';
import { usePermission } from '../../context/PermissionContext';
import UniversalList from '../../components/UniversalList';

const CustomerList = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const navigate = useNavigate();

  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const [stores, setStores] = useState([]);
  const [pulling, setPulling] = useState(false);
  const [pullMessage, setPullMessage] = useState(null);
  const [filters, setFilters] = useState({});
  const storeSelectRef = useRef(null);
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);
  const [countries, setCountries] = useState([]);

  const buildFilterParams = (extraFilters) => {
    const params = new URLSearchParams({ page, page_size: pageSize });
    if (search) params.append('search', search);
    const f = { ...extraFilters };
    Object.entries(f).forEach(([key, val]) => {
      if (val !== '' && val !== null && val !== undefined) {
        params.append(key, val);
      }
    });
    return params;
  };

  const fetchCustomers = useCallback(async (extraFilters) => {
    setLoading(true);
    const activeFilters = extraFilters || filters;
    try {
      const params = buildFilterParams(activeFilters);
      const res = await fetch(`/api/customers?${params}`);
      const data = await res.json();
      if (data.status === 'success' && data.data) {
        setCustomers(data.data.data || []);
        setTotal(data.data.total || 0);
      }
    } catch (e) {
      console.error('Failed to fetch customers:', e);
    }
    setLoading(false);
  }, [page, pageSize, search, filters]);

  useEffect(() => { fetchCustomers(filters); }, [fetchCustomers]);

  useEffect(() => {
    fetch('/api/channel/stores')
      .then(r => r.json())
      .then(data => {
        if (data.status === 'success') setStores(data.data.stores || []);
      })
      .catch(console.error);
  }, []);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm(t('customers.confirm_delete'))) return;
    try {
      const res = await fetch(`/api/customers/delete/${id}`, { method: 'POST' });
      const data = await res.json();
      if (data.status === 'success') fetchCustomers();
    } catch (e) {
      console.error('Delete failed:', e);
    }
  }, [t, fetchCustomers]);

  const getStoreIdFromRef = () => storeSelectRef.current ? storeSelectRef.current.value : '';

  const handlePull = useCallback(async () => {
    const storeId = getStoreIdFromRef();
    if (!storeId) { alert(t('customers.select_store')); return; }
    if (!window.confirm(t('customers.pull_confirm'))) return;
    setPulling(true);
    setPullMessage(null);
    try {
      const res = await fetch('/api/customers/pull', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ store_id: parseInt(storeId, 10) }),
      });
      const data = await res.json();
      if (data.status === 'success') {
        setPullMessage(data.message);
        fetchCustomers();
      } else {
        alert(data.message || 'Pull failed');
      }
    } catch (e) {
      console.error('Pull failed:', e);
      alert('Pull failed: ' + e.message);
    }
    setPulling(false);
  }, [t, fetchCustomers]);

  const columns = useMemo(() => [
    { key: 'id', label: t('customers.id'), width: 70, minWidth: 60 },
    {
      key: 'email', label: t('customers.email'), width: 200, minWidth: 150,
      render: (val) => val ? (
        <a href={`mailto:${val}`} className="text-blue-600 hover:text-blue-700 dark:text-blue-400"
          onClick={e => e.stopPropagation()}>{val}</a>
      ) : '-'
    },
    { key: 'firstname', label: t('customers.firstname'), width: 120, minWidth: 80, render: (val) => val || '-' },
    { key: 'lastname', label: t('customers.lastname'), width: 120, minWidth: 80, render: (val) => val || '-' },
    {
      key: 'total_orders', label: t('customers.total_orders'), width: 80, minWidth: 60,
      className: 'text-center', render: (val) => val ?? 0
    },
    {
      key: 'last_order_date', label: t('customers.last_order_date'), width: 160, minWidth: 120,
      render: (val) => val ? val.substring(0, 10) : '-'
    },
    {
      key: 'last_pulled_at', label: t('customers.last_pulled_at'), width: 160, minWidth: 120,
      render: (val) => val ? val.substring(0, 19) : '-'
    },
    {
      key: 'created_at', label: t('customers.created_at'), width: 160, minWidth: 120,
      render: (val) => val ? val.substring(0, 19) : '-'
    },
    {
      key: '_actions', label: t('customers.actions'), width: 100, minWidth: 80,
      className: 'text-center',
      render: (_, row) => (
        <div className="flex items-center justify-center gap-1" onClick={e => e.stopPropagation()}>
          <button onClick={() => navigate(`/customers/view/${row.id}`)}
            className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
            title={t('customers.view')}>
            <Eye size={16} />
          </button>
          {hasPermission('customer', 'delete') && (
            <button onClick={() => handleDelete(row.id)}
              className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
              title={t('customers.delete')}>
              <Trash2 size={16} />
            </button>
          )}
        </div>
      ),
    },
  ], [t, hasPermission, navigate, handleDelete]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold dark:text-slate-100">{t('customers.title')}</h1>
      </div>

      {pullMessage && (
        <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-700 dark:text-green-400">
          {pullMessage}
        </div>
      )}

      <UniversalList
        columns={columns}
        data={customers}
        loading={loading}
        emptyMessage={t('customers.no_customers')}
        rowKey="id"
        searchTerm={search}
        onSearchChange={(v) => { setSearch(v); setPage(1); }}
        searchPlaceholder={t('customers.search_placeholder')}
        allColumns={columns.map(c => ({ key: c.key, label: c.label }))}
        page={page}
        totalItems={total}
        pageSize={pageSize}
        setPage={setPage}
        setPageSize={(s) => { setPageSize(s); setPage(1); }}
        filterExtra={
          <div className="flex items-center gap-2">
            <select
              ref={storeSelectRef}
              value={filters.store_id || ''}
              onChange={e => { setFilters({...filters, store_id: e.target.value}); setPage(1); }}
              className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white"
            >
              <option value="">{t('customers.all_stores')}</option>
              {(stores || []).map(s => (
                <option key={s.id} value={s.id}>{s.store_name || s.name || `#${s.id}`}</option>
              ))}
            </select>
            {hasPermission('customer', 'edit') && (
              <button
                onClick={handlePull}
                disabled={pulling || !filters.store_id}
                className="flex items-center gap-1.5 px-3 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-slate-400 text-white rounded-lg transition-colors"
              >
                <RefreshCw size={16} className={pulling ? 'animate-spin' : ''} />
                {t('customers.pull')}
              </button>
            )}
            <div className="relative">
              <button onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
                className="flex items-center gap-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 px-3 py-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 text-sm font-medium transition-colors dark:text-white">
                <Filter size={16} />{t('common.filters')}
                {isFiltersExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
              {isFiltersExpanded && (
                <div className="absolute right-0 top-full mt-1 w-[640px] bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-5 z-[9999] shadow-xl">
                  <div className="grid grid-cols-2 gap-4 max-h-[360px] overflow-y-auto">
                    {/* ── Customer fields ── */}
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">{t('customers.email')}</label>
                      <input type="text" value={filters.email || ''}
                        onChange={e => setFilters({...filters, email: e.target.value})}
                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm dark:bg-slate-800 dark:text-white" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">{t('customers.firstname')}</label>
                      <input type="text" value={filters.firstname || ''}
                        onChange={e => setFilters({...filters, firstname: e.target.value})}
                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm dark:bg-slate-800 dark:text-white" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">{t('customers.lastname')}</label>
                      <input type="text" value={filters.lastname || ''}
                        onChange={e => setFilters({...filters, lastname: e.target.value})}
                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm dark:bg-slate-800 dark:text-white" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">{t('customers.gender')}</label>
                      <select value={filters.gender !== undefined ? filters.gender : ''}
                        onChange={e => setFilters({...filters, gender: e.target.value})}
                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm dark:bg-slate-800 dark:text-white">
                        <option value="">{t('common.all')}</option>
                        <option value="0">{t('customers.male')}</option>
                        <option value="1">{t('customers.female')}</option>
                        <option value="2">{t('customers.unspecified')}</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">{t('customers.taxvat')}</label>
                      <input type="text" value={filters.taxvat || ''}
                        onChange={e => setFilters({...filters, taxvat: e.target.value})}
                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm dark:bg-slate-800 dark:text-white" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">{t('customers.dob')} From</label>
                      <input type="date" value={filters.dob_from || ''}
                        onChange={e => setFilters({...filters, dob_from: e.target.value})}
                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm dark:bg-slate-800 dark:text-white" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">{t('customers.dob')} To</label>
                      <input type="date" value={filters.dob_to || ''}
                        onChange={e => setFilters({...filters, dob_to: e.target.value})}
                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm dark:bg-slate-800 dark:text-white" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">{t('customers.total_orders')} Min</label>
                      <input type="number" value={filters.total_orders_min || ''}
                        onChange={e => setFilters({...filters, total_orders_min: e.target.value})}
                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm dark:bg-slate-800 dark:text-white" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">{t('customers.total_orders')} Max</label>
                      <input type="number" value={filters.total_orders_max || ''}
                        onChange={e => setFilters({...filters, total_orders_max: e.target.value})}
                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm dark:bg-slate-800 dark:text-white" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">{t('customers.created_at')} From</label>
                      <input type="date" value={filters.created_at_from || ''}
                        onChange={e => setFilters({...filters, created_at_from: e.target.value})}
                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm dark:bg-slate-800 dark:text-white" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">{t('customers.created_at')} To</label>
                      <input type="date" value={filters.created_at_to || ''}
                        onChange={e => setFilters({...filters, created_at_to: e.target.value})}
                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm dark:bg-slate-800 dark:text-white" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">{t('customers.last_order_date')} From</label>
                      <input type="date" value={filters.last_order_date_from || ''}
                        onChange={e => setFilters({...filters, last_order_date_from: e.target.value})}
                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm dark:bg-slate-800 dark:text-white" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">{t('customers.last_order_date')} To</label>
                      <input type="date" value={filters.last_order_date_to || ''}
                        onChange={e => setFilters({...filters, last_order_date_to: e.target.value})}
                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm dark:bg-slate-800 dark:text-white" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">{t('customers.last_pulled_at')} From</label>
                      <input type="date" value={filters.last_pulled_at_from || ''}
                        onChange={e => setFilters({...filters, last_pulled_at_from: e.target.value})}
                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm dark:bg-slate-800 dark:text-white" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">{t('customers.last_pulled_at')} To</label>
                      <input type="date" value={filters.last_pulled_at_to || ''}
                        onChange={e => setFilters({...filters, last_pulled_at_to: e.target.value})}
                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm dark:bg-slate-800 dark:text-white" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">{t('customers.m2_created_at')} From</label>
                      <input type="date" value={filters.m2_created_at_from || ''}
                        onChange={e => setFilters({...filters, m2_created_at_from: e.target.value})}
                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm dark:bg-slate-800 dark:text-white" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">{t('customers.m2_created_at')} To</label>
                      <input type="date" value={filters.m2_created_at_to || ''}
                        onChange={e => setFilters({...filters, m2_created_at_to: e.target.value})}
                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm dark:bg-slate-800 dark:text-white" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">{t('customers.updated_at')} From</label>
                      <input type="date" value={filters.updated_at_from || ''}
                        onChange={e => setFilters({...filters, updated_at_from: e.target.value})}
                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm dark:bg-slate-800 dark:text-white" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">{t('customers.updated_at')} To</label>
                      <input type="date" value={filters.updated_at_to || ''}
                        onChange={e => setFilters({...filters, updated_at_to: e.target.value})}
                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm dark:bg-slate-800 dark:text-white" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">{t('customers.channel_type')}</label>
                      <select value={filters.channel_type || ''}
                        onChange={e => setFilters({...filters, channel_type: e.target.value})}
                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm dark:bg-slate-800 dark:text-white">
                        <option value="">{t('common.all')}</option>
                        <option value="magento2">Magento 2</option>
                        <option value="shopify">Shopify</option>
                        <option value="custom">Custom</option>
                      </select>
                    </div>

                    {/* ── Address fields ── */}
                    <div className="col-span-2 border-t border-slate-200 dark:border-slate-700 pt-3 mt-1">
                      <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-2">{t('customers.addresses')}</p>
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">{t('customers.telephone')}</label>
                      <input type="text" value={filters.addr_telephone || ''}
                        onChange={e => setFilters({...filters, addr_telephone: e.target.value})}
                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm dark:bg-slate-800 dark:text-white" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">{t('customers.address_city')}</label>
                      <input type="text" value={filters.addr_city || ''}
                        onChange={e => setFilters({...filters, addr_city: e.target.value})}
                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm dark:bg-slate-800 dark:text-white" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">{t('customers.address_region')}</label>
                      <input type="text" value={filters.addr_region || ''}
                        onChange={e => setFilters({...filters, addr_region: e.target.value})}
                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm dark:bg-slate-800 dark:text-white" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">{t('customers.address_postcode')}</label>
                      <input type="text" value={filters.addr_postcode || ''}
                        onChange={e => setFilters({...filters, addr_postcode: e.target.value})}
                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm dark:bg-slate-800 dark:text-white" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">{t('customers.address_country')}</label>
                      <input type="text" value={filters.addr_country_id || ''}
                        onChange={e => setFilters({...filters, addr_country_id: e.target.value})}
                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm dark:bg-slate-800 dark:text-white"
                        placeholder="Country code (e.g. CN)" />
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 mt-4">
                    <button onClick={() => { setFilters({}); setIsFiltersExpanded(false); fetchCustomers({}); }}
                      className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors">
                      {t('common.reset_all')}
                    </button>
                    <button onClick={() => { fetchCustomers(filters); setIsFiltersExpanded(false); }}
                      className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 shadow-md transition-all">
                      {t('common.apply_filters')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        }
      />
    </div>
  );
};

export default CustomerList;
